import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, authFetch } from "@/App";
import {
  Users, Loader2, Play, Pause, RefreshCw, MessageSquare,
  BarChart3, Clock, Settings, Zap
} from "lucide-react";

const AGENTS = [
  { id: "miles", name: "Miles", role: "Community Engagement Director", color: "bg-emerald-500" },
  { id: "jasmine", name: "Jasmine", role: "Onboarding Specialist", color: "bg-blue-500" },
  { id: "marcus", name: "Marcus", role: "Growth Strategist", color: "bg-purple-500" },
  { id: "tanya", name: "Tanya", role: "Community Manager", color: "bg-pink-500" },
  { id: "derek", name: "Derek", role: "Networking Coordinator", color: "bg-amber-500" },
  { id: "simone", name: "Simone", role: "Opportunities Director", color: "bg-cyan-500" },
  { id: "kev", name: "Kev", role: "Points & Rewards Manager", color: "bg-orange-500" },
  { id: "whitney", name: "Whitney", role: "Mastermind Pod Director", color: "bg-rose-500" },
  { id: "andre", name: "Andre", role: "Member Success Manager", color: "bg-indigo-500" },
  { id: "carmen", name: "Carmen", role: "Weekly Digest Editor", color: "bg-teal-500" },
];

export function AgentManagementPanel({ open, onOpenChange }) {
  const [agentStatuses, setAgentStatuses] = useState({});
  const [agentStats, setAgentStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [triggering, setTriggering] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Get agent configs
      const statusRes = await authFetch(`${API}/consultant/agents/status`);
      if (statusRes.ok) {
        const d = await statusRes.json();
        const configs = {};
        (d.agents || []).forEach(a => { configs[a.agent_id] = a; });
        setAgentStatuses(configs);
      }

      // Get community stats
      const statsRes = await authFetch(`${API}/consultant/agents/stats`);
      if (statsRes.ok) setAgentStats(await statsRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  const toggleAgent = async (agentId) => {
    setToggling(agentId);
    try {
      const current = agentStatuses[agentId]?.enabled !== false;
      const res = await authFetch(`${API}/consultant/agents/toggle`, {
        method: "POST",
        body: JSON.stringify({ agent_id: agentId, enabled: !current }),
      });
      if (res.ok) {
        toast.success(`${agentId} ${!current ? "enabled" : "paused"}`);
        fetchData();
      }
    } catch { toast.error("Failed"); }
    setToggling(null);
  };

  const triggerAgent = async (agentId) => {
    setTriggering(agentId);
    try {
      const res = await authFetch(`${API}/consultant/agents/trigger`, {
        method: "POST",
        body: JSON.stringify({ agent_id: agentId }),
      });
      if (res.ok) {
        toast.success(`${agentId} triggered`);
        fetchData();
      } else {
        toast.error("Agent run failed");
      }
    } catch { toast.error("Failed"); }
    setTriggering(null);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0" data-testid="agent-management-panel">
        <DialogHeader className="p-5 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#2BC40E]" /> AI Agent Management
          </DialogTitle>
          <DialogDescription>Toggle, trigger, and monitor all 10 AI business agents</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#39FF14]" /></div>
          ) : (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-3 mb-6">
                <Card className="border-neutral-200 bg-neutral-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-neutral-900">{AGENTS.length}</p>
                    <p className="text-[10px] text-neutral-500">Total Agents</p>
                  </CardContent>
                </Card>
                <Card className="border-neutral-200 bg-green-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {AGENTS.filter(a => agentStatuses[a.id]?.enabled !== false).length}
                    </p>
                    <p className="text-[10px] text-green-600">Active</p>
                  </CardContent>
                </Card>
                <Card className="border-neutral-200 bg-amber-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-amber-600">
                      {agentStats.total_posts || 0}
                    </p>
                    <p className="text-[10px] text-amber-600">Posts Created</p>
                  </CardContent>
                </Card>
                <Card className="border-neutral-200 bg-blue-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {agentStats.total_interactions || 0}
                    </p>
                    <p className="text-[10px] text-blue-600">Interactions</p>
                  </CardContent>
                </Card>
              </div>

              {/* Agent Grid */}
              <div className="grid md:grid-cols-2 gap-3">
                {AGENTS.map(agent => {
                  const config = agentStatuses[agent.id] || {};
                  const enabled = config.enabled !== false;
                  const lastRun = config.last_run || config.last_activity;

                  return (
                    <Card key={agent.id} className={`border-neutral-200 transition-all ${!enabled ? "opacity-50" : ""}`}
                      data-testid={`agent-panel-${agent.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          {/* Avatar */}
                          <div className={`w-10 h-10 rounded-lg ${agent.color} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                            {agent.name[0]}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-sm text-neutral-900">{agent.name}</p>
                              <Badge className={enabled ? "bg-green-100 text-green-700 text-[10px]" : "bg-neutral-100 text-neutral-500 text-[10px]"}>
                                {enabled ? "Active" : "Paused"}
                              </Badge>
                            </div>
                            <p className="text-xs text-neutral-500">{agent.role}</p>
                            {lastRun && (
                              <p className="text-[10px] text-neutral-400 flex items-center gap-1 mt-0.5">
                                <Clock className="w-2.5 h-2.5" /> Last: {typeof lastRun === 'string' ? lastRun.slice(0, 16).replace("T", " ") : "—"}
                              </p>
                            )}
                          </div>

                          {/* Controls */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                              onClick={() => triggerAgent(agent.id)}
                              disabled={triggering === agent.id || !enabled}
                              title="Run now">
                              {triggering === agent.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5 text-[#2BC40E]" />}
                            </Button>
                            <Switch
                              checked={enabled}
                              onCheckedChange={() => toggleAgent(agent.id)}
                              disabled={toggling === agent.id}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Refresh */}
              <div className="text-center mt-4">
                <Button variant="ghost" size="sm" onClick={fetchData} className="text-neutral-500">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AgentManagementPanel;
